/**
 * apply-theme.ts — DOM injection layer for dynamic theming.
 *
 * Provides two main functions:
 *   - applyTheme()  : generates tokens from a source color and sets them
 *                     as CSS custom properties on document.documentElement
 *   - clearTheme()  : removes all runtime overrides, reverting to the
 *                     SCSS-compiled defaults
 *
 * How it works:
 *   Setting `documentElement.style.setProperty('--rd-sys-color-*', value)`
 *   overrides the stylesheet-declared `:root { --rd-sys-color-*: ... }`
 *   because inline custom properties have higher specificity.
 *   Calling `removeProperty()` reverts to the stylesheet default.
 *
 *   The public `--ts-var-*` bridge is the exception: those are shared with the
 *   embed customer, whose customCSSUrl is a stylesheet an inline prop would
 *   always beat, so when a URL is present they are published only through the
 *   app theme <style> element (see hasCustomerCssUrl).
 *
 * Works with the existing [data-theme='dark'] toggle — when dark mode is
 * active, we simply generate and inject the dark-mode token set instead.
 */

import {
    getEmbedCssParams,
    getEmbedCustomCssUrl,
    setAppThemeCssVariables,
} from '@thoughtspot/embed-util';
import {
    prepareTopNavInternalVariables,
    TopNavInternalVariablesProps,
} from '../top-nav-v3/top-nav-v3-theme-utils';
import { GeneratedTheme, generateTheme } from './theme-generator';
import {
    COMP_TOKEN_PREFIX,
    COMPONENT_TOKEN_NAMES,
    SYS_TOKEN_PREFIX,
    SYSTEM_TOKEN_NAMES,
} from './token-names';

// =========================================================================
// Brand color normalization
// =========================================================================

const VALID_BRAND_HEX = /^#[0-9a-f]{6}$/;

/**
 * Normalize a persisted/event-supplied brand color to "#rrggbb", or null if
 * it cannot be interpreted as a hex color.
 *
 * Legacy formats provably exist in stored appPanelColor values (the DARK
 * sentinel itself is "#1D232FFF"), and the Material parser this engine
 * replaced accepted 3- and 8-digit hex — so alpha suffixes and shorthand
 * must keep theming instead of throwing during app bootstrap. This is also
 * the input-validation choke point before any value reaches
 * style.setProperty or the embed CSS <style> serialization.
 */
export function normalizeBrandColor(input: string): string | null {
    if (typeof input !== 'string') return null;
    let hex = input.trim().toLowerCase();
    if (!hex.startsWith('#')) hex = `#${hex}`;
    // #rgb / #rgba shorthand → expand (alpha digit dropped below)
    if (/^#[0-9a-f]{3,4}$/.test(hex)) {
        hex = `#${hex
            .slice(1, 4)
            .split('')
            .map(c => c + c)
            .join('')}`;
    }
    // #rrggbbaa → strip alpha
    if (/^#[0-9a-f]{8}$/.test(hex)) {
        hex = hex.slice(0, 7);
    }
    return VALID_BRAND_HEX.test(hex) ? hex : null;
}

// =========================================================================
// External variable bridge
// =========================================================================
//
// Many legacy components use --ts-var-* CSS variables (the "embed
// customization" layer) instead of the --rd-* radiant design tokens.
// These chain through --ts-var-root-color, which is declared as `initial`
// in _external-variables.scss. When `initial` (guaranteed-invalid in CSS
// custom properties), vars resolve to fallbacks — but some elements in the
// inheritance chain have NO radiant fallback, causing dark text on dark bg.
//
// We bridge this gap by also setting the key --ts-var-* variables when
// a dynamic theme is applied. This single variable cascades to:
//   - --ts-var-search-bar-text-font-color (search token text)
//   - --ts-var-axis-data-label-color (KPI hero text)
//   - --ts-var-nav-color, --ts-var-nav-text-color
//   - --ts-var-chip-color, --ts-var-chip--hover-color
//   - --ts-var-menu-color, --ts-var-dialog-body-color, etc.
//   - Plus ~30 more variables declared in _external-variables.scss
//
// See: js/ts-packages/radiant-styles/css/_external-variables.scss
// =========================================================================

/**
 * External variables that need to be bridged to the radiant token system.
 * Maps an external CSS variable name to the system or component token it
 * should mirror. Every referenced token must resolve to an OPAQUE hex —
 * legacy --ts-var-* consumers cannot be assumed to handle rgba() values.
 *
 * NOT bridged, deliberately: --ts-var-root-background and the liveboard
 * surfaces that inherit from it (--ts-var-liveboard-layout-background,
 * --ts-var-liveboard-tile-background). Liveboard styling writes those as an
 * inline style on the liveboard, and it drops its own value whenever :root
 * carries one — that is how an embed customer's `customizations.style` keeps
 * precedence. A pin here reads as exactly such an override, so it deleted
 * every liveboard colour pick as soon as a brand colour existed (SCAL-337252).
 * Every consumer of the three already falls back to the radiant token the
 * generator regenerates, so the brand theme still colours these surfaces.
 *
 * --ts-var-root-color cascade audit: the vars that chain from it in
 * _external-variables.scss (nav-text, search-bar text, chip text, menu,
 * dialog, axis labels, viz title, home-card, liveboard-header font, etc.)
 * are all plain-text roles for which content-primary is correct. The two
 * button vars below are the exceptions — semantically they are NOT text
 * colors, so the chain would wrongly flatten them to content-primary
 * (e.g. tertiary/link buttons turning white in dark mode).
 */
const EXTERNAL_VARIABLE_BRIDGE: ReadonlyArray<{
    /** The --ts-var-* CSS variable to set */
    externalVar: string;
    /** The system token name whose value should be used */
    systemToken?: string;
    /** The component token name whose value should be used */
    componentToken?: string;
    /**
     * Skip this pin when the embed customer set --ts-var-root-color:
     * _external-variables.scss cascades root-color into this var, and an
     * inline pin on <html> would sever that documented cascade.
     */
    respectRootCascade?: boolean;
    /**
     * Slot of the legacy top-nav derivation that supplies this var when
     * "apply as top nav color" is on, so the nav matches the pre-radiant look.
     */
    navLiteralSlot?: 'background' | 'color';
}> = [
    // Root text color — cascades to ~30+ --ts-var-* variables
    // (nav, chip, menu, dialog, axis labels, search bar, etc.)
    { externalVar: '--ts-var-root-color', systemToken: 'content-primary' },

    // Primary button text: _external-variables.scss leaves this `initial`,
    // and the radiant fallback is content-inverse — which is NOT
    // contrast-checked against a custom brand color. Bridge it to the
    // generator's contrast-picked CTA text so labels stay readable (e.g.
    // dark text on a light-yellow brand CTA).
    {
        externalVar: '--ts-var-button--primary-color',
        componentToken: 'button-primary-content',
        respectRootCascade: true,
    },

    // Tertiary/link button text: chains from --ts-var-root-color in
    // _external-variables.scss, which the bridge above sets to
    // content-primary — flattening every text button (e.g. the filter
    // dialog's "Reset to default") to plain white/black. Pin it back to
    // the brand text color the radiant fallback intends.
    {
        externalVar: '--ts-var-button--tertiary-color',
        systemToken: 'content-brand',
        respectRootCascade: true,
    },

    // Navigation bar colors
    {
        externalVar: '--ts-var-nav-background',
        systemToken: 'background-on-base',
        navLiteralSlot: 'background',
    },
    {
        externalVar: '--ts-var-nav-color',
        systemToken: 'content-primary',
        navLiteralSlot: 'color',
    },

    // Sage (search bar) header background
    {
        externalVar: '--ts-var-sage-bar-header-background-color',
        systemToken: 'background-base',
    },

    // Menu dropdown surface & text
    {
        externalVar: '--ts-var-menu-background',
        systemToken: 'background-raised',
    },
    { externalVar: '--ts-var-menu-color', systemToken: 'content-primary' },

    // Dialog / modal surfaces & text — each var mirrors the token the
    // radiant Modal uses as its own fallback (modal.module.scss): header
    // and body sit on background-base, the footer on background-on-base.
    // Flattening them to one token makes the footer indistinguishable
    // from the body.
    {
        externalVar: '--ts-var-dialog-header-background',
        systemToken: 'background-base',
    },
    {
        externalVar: '--ts-var-dialog-body-background',
        systemToken: 'background-base',
    },
    {
        externalVar: '--ts-var-dialog-body-color',
        systemToken: 'content-primary',
    },
    {
        externalVar: '--ts-var-dialog-header-color',
        systemToken: 'content-primary',
    },
    {
        externalVar: '--ts-var-dialog-footer-background',
        systemToken: 'background-on-base',
    },

    // Viz title / description text
    { externalVar: '--ts-var-viz-title-color', systemToken: 'content-primary' },
    {
        externalVar: '--ts-var-viz-description-color',
        systemToken: 'content-secondary',
    },
];

// =========================================================================
// App-level theme metadata CSS variables
// =========================================================================
//
// These CSS variables are the single source of truth for the active theme
// at the CSS level. Any code (JS or CSS) can read them to know:
//   1. What type of theme is active (custom / dark / two-tone / none)
//   2. What the source brand color is (only set when type=custom)
//
// Usage in JS:
//   getComputedStyle(document.documentElement).getPropertyValue('--ts-app-source-color')
//
// Usage in CSS (future):
//   background: var(--ts-app-source-color, #2770ef);
// =========================================================================

/** The CSS variable that holds the current source/brand color hex. */
const APP_SOURCE_COLOR_VAR = '--ts-app-source-color';

/** The CSS variable that holds the active theme type (custom / dark / two-tone / none). */
const APP_THEME_TYPE_VAR = '--ts-app-theme-type';

// =========================================================================
// Internal state
// =========================================================================

/** Tracks whether a custom theme is currently active. */
let customThemeActive = false;

/** Stores the current source color for re-application on mode switch. */
let currentSourceHex: string | null = null;

/**
 * Whether the top nav takes the literal brand hex. Module state alongside
 * currentSourceHex so a mode toggle preserves it without the caller resending.
 */
let currentApplyAsTopNav = false;

/** Cached generated theme to avoid re-computation on mode toggle. */
let cachedTheme: GeneratedTheme | null = null;

/** Cascade-break vars pinned by the last apply, so a re-apply can drop them. */
let currentCascadeBreakVars: readonly string[] = [];

/**
 * True when the embed CUSTOMER set this variable through
 * customizations.style.customCSS.variables.
 *
 * This is a LIVE read, not a snapshot: getEmbedCssParams reports only the
 * customer's own variables (the app's generated theme vars go to a separate
 * store via setAppThemeCssVariables), so our own values can never echo back
 * here and be mistaken for a customer override. Reading live also means a
 * customizations update that arrives after the first apply — e.g. via the
 * UpdateEmbedParams host event — is respected instead of being frozen out.
 * @param varName - CSS custom property name
 */
export function isCustomerOwned(varName: string): boolean {
    return !!getEmbedCssParams(varName);
}

/** The embed host's --ts-var-nav-background as hex, else null. */
export function getHostNavBackground(): string | null {
    const value = getEmbedCssParams('--ts-var-nav-background');
    return typeof value === 'string' ? normalizeBrandColor(value) : null;
}

/**
 * True when the embed CUSTOMER supplied customizations.style.customCSSUrl.
 *
 * Per-variable ownership is unknowable for that channel — the file is fetched
 * by the browser and its declarations never reach JS — so isCustomerOwned()
 * cannot see them. Instead we change HOW our bridged values are published:
 * an inline prop on :root outranks every stylesheet, so with a URL present we
 * publish through the app theme <style> element only (setAppThemeCssVariables
 * keeps it before the customer's <link>), letting the cascade give
 *   customCSS.variables  >  customCSSUrl  >  app theme
 * Nothing is lost by dropping the inline pin: --ts-var-* are only ever read
 * as var(--ts-var-x, <fallback>) and are never declared in our own SCSS, so
 * the theme <style> element is enough to make them take effect.
 */
function hasCustomerCssUrl(): boolean {
    return !!getEmbedCustomCssUrl();
}

// =========================================================================
// Public API
// =========================================================================

/**
 * Generate a full M3 color theme from a source color and apply it to the DOM.
 *
 * This sets --rd-sys-color-* and --rd-comp-color-* CSS custom properties
 * on document.documentElement, which override the SCSS defaults and cause
 * every component using these tokens to update instantly.
 *
 * @param sourceHex - Source/brand color hex (e.g., "#2770ef"); legacy
 *                    formats (#rgb, #rrggbbaa, missing '#') are normalized,
 *                    anything else is logged and ignored (no-op).
 * @param options   - isDark forces dark tokens; applyAsTopNavColor paints the
 *                    top nav with the literal brand hex (omit it to keep
 *                    whatever the last call set); cascadeBreakVars lists
 *                    --ts-var-* leaves to sever from the bridged
 *                    --ts-var-root-color (see CASCADE BREAK below).
 *
 * @example
 * ```ts
 * // Apply a purple theme in current mode
 * applyTheme('#8C62F5');
 *
 * // Apply a purple theme explicitly in dark mode
 * applyTheme('#8C62F5', { isDark: true });
 * ```
 */
export function applyTheme(
    rawSourceHex: string,
    options: {
        isDark?: boolean;
        applyAsTopNavColor?: boolean;
        cascadeBreakVars?: readonly string[];
    } = {},
): Record<string, string> {
    // Collects every CSS variable set by this call. Returned to the
    // caller so it can feed them into setAppThemeCssVariables (the embed
    // CSS infrastructure) without scraping the DOM.
    const appliedVars: Record<string, string> = {};

    // Validate/normalize before anything touches the DOM: a bad persisted
    // color must degrade to the default theme, never crash app bootstrap
    // (blink-v2 CLAUDE.md: all init-path errors must be caught and logged).
    const sourceHex = normalizeBrandColor(rawSourceHex);
    if (!sourceHex) {
        // eslint-disable-next-line no-console
        console.error(
            `[dynamic-theme] Ignoring invalid brand color "${rawSourceHex}"`,
        );
        return appliedVars;
    }

    // undefined means "unchanged" so reapplyThemeForMode / a plain mode
    // toggle can't silently drop the admin's choice.
    if (options.applyAsTopNavColor !== undefined) {
        currentApplyAsTopNav = options.applyAsTopNavColor;
    }

    // Determine current mode from data-theme attribute if not explicitly set
    const isDark =
        options.isDark ??
        document.documentElement.getAttribute('data-theme') === 'dark';

    // Only regenerate if the source color changed
    if (sourceHex !== currentSourceHex || cachedTheme === null) {
        cachedTheme = generateTheme(sourceHex);
    }

    // Pick the correct mode's tokens
    const mode = isDark ? 'dark' : 'light';

    // Guard: cachedTheme is guaranteed non-null here (either just generated
    // above, or was already non-null from a previous call), but we guard
    // explicitly to satisfy the linter's no-non-null-assertion rule.
    if (!cachedTheme) {
        return appliedVars;
    }

    const systemTokens = cachedTheme.system[mode];
    const componentTokens = cachedTheme.component[mode];

    const root = document.documentElement;

    // Inject system tokens
    Object.entries(systemTokens).forEach(([name, value]) => {
        const varName = `${SYS_TOKEN_PREFIX}${name}`;
        root.style.setProperty(varName, value);
        appliedVars[varName] = value;
    });

    // Inject component tokens. (The embed-customer precedence for the
    // generator-only button-primary-disabled token is handled declaratively
    // by the var() fallback order in radiant-react button.module.scss —
    // --ts-var-button--primary-background is read first there.)
    Object.entries(componentTokens).forEach(([name, value]) => {
        const varName = `${COMP_TOKEN_PREFIX}${name}`;
        root.style.setProperty(varName, value);
        appliedVars[varName] = value;
    });

    // Bridge external --ts-var-* variables that don't use radiant fallbacks.
    // Without this, components using --ts-var-root-color (KPI, chart selector,
    // search tokens, etc.) get dark text on dark backgrounds in dark mode.
    //
    // IMPORTANT: Skip bridging when the embed CUSTOMER owns the variable —
    // customer overrides take priority over dynamic theming. A customCSSUrl
    // cannot be inspected per variable, so there we keep bridging but publish
    // via the app theme <style> element instead of an inline pin, and let
    // document order settle it (see hasCustomerCssUrl).
    const customerCssUrl = hasCustomerCssUrl();
    // Literal mode derives the nav vars from the nav colour; a host nav
    // background wins over the brand.
    const hostNavBackground = getHostNavBackground();
    let navLiteral: TopNavInternalVariablesProps | null = null;
    if (hostNavBackground) {
        navLiteral = prepareTopNavInternalVariables(hostNavBackground);
    } else if (currentApplyAsTopNav) {
        navLiteral = prepareTopNavInternalVariables(sourceHex);
    }

    EXTERNAL_VARIABLE_BRIDGE.forEach(
        ({
            externalVar,
            systemToken,
            componentToken,
            respectRootCascade,
            navLiteralSlot,
        }) => {
            // Drop any pin we made on an earlier apply: customizations can
            // arrive after the first theme apply (UpdateEmbedParams host
            // event), and a stale inline pin outranks the customer's <style>.
            if (isCustomerOwned(externalVar)) {
                root.style.removeProperty(externalVar);
                return;
            }
            // Leaf vars that _external-variables.scss derives from
            // --ts-var-root-color: when the customer set the cascade ROOT,
            // let their cascade win instead of pinning the leaf inline.
            if (respectRootCascade && isCustomerOwned('--ts-var-root-color')) {
                root.style.removeProperty(externalVar);
                return;
            }

            let value: string | undefined;
            if (navLiteral && navLiteralSlot) {
                value =
                    navLiteralSlot === 'background'
                        ? navLiteral.topNavBackground
                        : navLiteral.topNavColor;
            } else if (systemToken) {
                value = systemTokens[systemToken];
            } else {
                value = componentTokens[componentToken ?? ''];
            }
            if (!value) {
                return;
            }
            // Always publish through the theme <style> element…
            appliedVars[externalVar] = value;
            if (customerCssUrl) {
                // …and, with a customer stylesheet file in play, ONLY there:
                // an inline pin would outrank the file. Also drop a pin left
                // by an apply that ran before the URL arrived.
                root.style.removeProperty(externalVar);
                return;
            }
            root.style.setProperty(externalVar, value);
        },
    );

    // --- Cascade break ----------------------------------------------------
    // Sever named leaves from the root-color pin above: a consumer resolving
    // an admin-style value through `var(--ts-var-x, <admin>)` only reaches
    // that fallback while the leaf is guaranteed-invalid. Same customer
    // guards as the bridge, so an embed/liveboard override still wins.
    // Omitting the option keeps the previous list — the admin brand picker
    // re-applies through global-api without one.
    currentCascadeBreakVars.forEach(externalVar => {
        root.style.removeProperty(externalVar);
    });
    currentCascadeBreakVars =
        options.cascadeBreakVars ?? currentCascadeBreakVars;
    currentCascadeBreakVars.forEach(externalVar => {
        if (
            isCustomerOwned(externalVar) ||
            isCustomerOwned('--ts-var-root-color')
        ) {
            return;
        }
        appliedVars[externalVar] = 'initial';
        if (customerCssUrl) {
            return;
        }
        root.style.setProperty(externalVar, 'initial');
    });

    // Set app-level metadata CSS variables.
    // --ts-app-source-color is THE single source of truth for the brand color.
    // Downstream CSS or JS can reference it to know the active brand color.
    root.style.setProperty(APP_SOURCE_COLOR_VAR, sourceHex);
    root.style.setProperty(APP_THEME_TYPE_VAR, 'custom');
    appliedVars[APP_SOURCE_COLOR_VAR] = sourceHex;
    appliedVars[APP_THEME_TYPE_VAR] = 'custom';

    // Update internal state
    customThemeActive = true;
    currentSourceHex = sourceHex;

    return appliedVars;
}

/**
 * Remove all runtime theme overrides, reverting to SCSS-compiled defaults.
 *
 * Two-pass approach:
 *   1. Remove known token names from the named arrays (fast path).
 *   2. Sweep all inline properties on :root and remove any that match our
 *      prefixes (--rd-sys-color-*, --rd-comp-color-*, --ts-var-*, --ts-app-*).
 *      This catches properties that may have been set outside the named
 *      arrays or by external code paths.
 *
 * After removal the browser falls back to the stylesheet-declared values.
 */
export function clearTheme(): void {
    const root = document.documentElement;

    // --- Pass 1: Named token removal (fast path) -------------------------

    // Remove system token overrides
    SYSTEM_TOKEN_NAMES.forEach(name => {
        root.style.removeProperty(`${SYS_TOKEN_PREFIX}${name}`);
    });

    // Remove component token overrides
    COMPONENT_TOKEN_NAMES.forEach(name => {
        root.style.removeProperty(`${COMP_TOKEN_PREFIX}${name}`);
    });

    // Remove bridged external variables that WE set (i.e. not customer-owned).
    // Genuine embed-customer overrides live in the embed <style> element,
    // never as inline props here, so this leaves them intact.
    EXTERNAL_VARIABLE_BRIDGE.forEach(({ externalVar }) => {
        if (!isCustomerOwned(externalVar)) {
            root.style.removeProperty(externalVar);
        }
    });
    currentCascadeBreakVars.forEach(externalVar => {
        if (!isCustomerOwned(externalVar)) {
            root.style.removeProperty(externalVar);
        }
    });

    // Clear app-level metadata CSS variables
    root.style.removeProperty(APP_SOURCE_COLOR_VAR);
    root.style.removeProperty(APP_THEME_TYPE_VAR);

    // --- Pass 2: Defensive sweep -----------------------------------------
    // Iterate ALL inline style properties on :root and remove any that match
    // our known prefixes. This guarantees nothing is left behind, even if the
    // named arrays are out of sync or if other code set extra variables.
    //
    // IMPORTANT: a --ts-var-* prop is removed unless the snapshot marks it
    // embed-customer-owned. Anything the customer genuinely set lives in the
    // embed <style> element, not inline, so this only ever clears our own
    // inline bridged vars.
    const allInlineProps = Array.from(
        { length: root.style.length },
        (_, i) => root.style[i],
    );
    const propsToRemove = allInlineProps.filter(prop => {
        if (
            prop.startsWith(SYS_TOKEN_PREFIX) ||
            prop.startsWith(COMP_TOKEN_PREFIX) ||
            prop.startsWith('--ts-app-')
        ) {
            return true;
        }
        if (prop.startsWith('--ts-var-')) {
            return !isCustomerOwned(prop);
        }
        return false;
    });
    propsToRemove.forEach(prop => {
        root.style.removeProperty(prop);
    });

    // Reset internal state. The embed-owned snapshot is intentionally NOT
    // reset: the customer's owned vars don't change because we cleared our
    // theme, and re-taking it after we've polluted the store would be wrong.
    customThemeActive = false;
    currentSourceHex = null;
    cachedTheme = null;
    currentCascadeBreakVars = [];
    currentApplyAsTopNav = false;
}

/**
 * Re-apply the current custom theme for a different mode (light/dark).
 *
 * Call this from the dark mode toggle when a custom theme is active.
 * Uses the cached theme (no re-generation) for instant mode switch.
 *
 * @param isDark - Whether to apply dark mode tokens
 */
export function reapplyThemeForMode(isDark: boolean): void {
    if (!customThemeActive || !currentSourceHex) {
        return; // No custom theme active — nothing to do
    }
    // Republish into the app theme <style> element too: with a customCSSUrl
    // present the bridged vars live ONLY there, so a re-apply that skipped
    // this would leave the previous mode's colors on the page.
    setAppThemeCssVariables(
        applyTheme(currentSourceHex, {
            isDark,
            cascadeBreakVars: currentCascadeBreakVars,
        }),
    );
}

/**
 * Check if a custom theme is currently active.
 *
 * Useful for the dark mode toggle to know whether it should
 * call reapplyThemeForMode().
 */
export function isCustomThemeActive(): boolean {
    return customThemeActive;
}

/**
 * Get the current source color hex, or null if no custom theme is active.
 */
export function getCurrentSourceColor(): string | null {
    return currentSourceHex;
}

/**
 * Whether the top nav is currently painted with the literal brand hex
 * ("apply as top nav color") rather than the generated neutral surface.
 */
export function isNavColorLiteral(): boolean {
    return currentApplyAsTopNav;
}

/**
 * Read the active source color from the CSS variable on :root.
 * Returns null if no custom theme is active.
 * Useful for code that doesn't have access to the JS module state.
 */
export function getSourceColorFromCSS(): string | null {
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(APP_SOURCE_COLOR_VAR)
        .trim();
    return value || null;
}

// Re-export the CSS variable names for consumers that need them
export { APP_SOURCE_COLOR_VAR, APP_THEME_TYPE_VAR };

/**
 * Test-only: reset all module-level state so each spec starts from a clean
 * slate.
 */
export function resetDynamicThemeStateForTests(): void {
    customThemeActive = false;
    currentSourceHex = null;
    cachedTheme = null;
    currentCascadeBreakVars = [];
    currentApplyAsTopNav = false;
}
